import React, { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaCircle,
  FaClock,
  FaExclamationCircle,
  FaInbox,
  FaPaperclip,
  FaPlay,
  FaSearch,
  FaTicketAlt,
  FaUpload,
  FaUserCog,
  FaWrench
} from 'react-icons/fa';
import telegramMiniAppService from '../services/telegramMiniAppService';
import { formatDateTime } from '../utils/format';
import { translateIssueTypes } from '../utils/issueTypes';
import '../styles/telegram-mini-app.css';

const STATUS_LABELS = {
  new: 'جديدة',
  assigned: 'مسندة',
  in_progress: 'قيد العمل',
  on_hold: 'معلقة',
  resolved: 'محلولة',
  closed: 'مغلقة'
};

const PRIORITY_LABELS = {
  low: 'منخفضة',
  medium: 'متوسطة',
  high: 'عالية',
  urgent: 'عاجلة'
};

const ROLE_LABELS = {
  technician: 'فني',
  supervisor: 'مشرف',
  admin: 'مدير'
};

const PANELS = {
  OVERVIEW: 'overview',
  UPDATE: 'update',
  RESOLVE: 'resolve'
};

const TELEGRAM_BOOT_TIMEOUT_MS = 2200;
const TELEGRAM_BOOT_POLL_INTERVAL_MS = 150;
const TELEGRAM_BOT_USERNAME = (process.env.REACT_APP_TELEGRAM_BOT_USERNAME || 'ICT_Support_System_bot').replace(/^@/, '');

const getStatusLabel = (status) => STATUS_LABELS[status] || status || '-';
const getPriorityLabel = (priority) => PRIORITY_LABELS[priority] || priority || '-';
const getRoleLabel = (role) => ROLE_LABELS[role] || role || 'فني';

const getTicketSearchValue = (ticket) => [
  ticket.ticket_number,
  ticket.short_description,
  ticket.branch_name,
  translateIssueTypes(ticket.issue_types)
].join(' ').toLowerCase();

const buildTelegramThemeVars = (themeParams = {}) => {
  const accent = themeParams.button_color || themeParams.link_color || '#1b998b';

  return {
    '--tg-app-bg': '#f4f8fc',
    '--tg-app-bg-soft': '#eaf5ff',
    '--tg-app-surface': '#ffffff',
    '--tg-app-surface-soft': '#f7fbff',
    '--tg-app-card': '#ffffff',
    '--tg-app-card-alt': '#f6fbff',
    '--tg-app-text': '#17324d',
    '--tg-app-heading': '#10243b',
    '--tg-app-muted': '#6b7d90',
    '--tg-app-link': accent,
    '--tg-app-accent': accent,
    '--tg-app-accent-strong': '#157a70',
    '--tg-app-accent-soft': '#dff5f1',
    '--tg-app-info-soft': '#eaf3ff',
    '--tg-app-warning-soft': '#fff3dd',
    '--tg-app-danger-soft': '#feebee',
    '--tg-app-stroke': '#dbe7f2',
    '--tg-app-shadow': 'rgba(19, 43, 69, 0.12)'
  };
};

const applyTelegramTheme = (themeParams = {}) => {
  const root = document.documentElement;
  Object.entries(buildTelegramThemeVars(themeParams)).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
};

const parseTicketIdFromStartParam = (startParam) => {
  if (!startParam) {
    return null;
  }

  const normalized = String(startParam).trim();
  if (!normalized) {
    return null;
  }

  const directMatch = normalized.match(/^\d+$/);
  if (directMatch) {
    return directMatch[0];
  }

  const ticketMatch = normalized.match(/^ticket[:_-]?(\d+)$/i);
  return ticketMatch?.[1] || null;
};

const getTelegramBotUrl = () => {
  if (!TELEGRAM_BOT_USERNAME) {
    return null;
  }

  return `https://t.me/${TELEGRAM_BOT_USERNAME}`;
};

const logMiniAppStage = (stage, details = {}) => {
  // Keep launch diagnostics visible in Telegram WebView dev tools when needed.
  console.info('[TelegramMiniApp]', stage, details);
};

const TelegramMiniApp = () => {
  const [searchParams] = useSearchParams();
  const initialTicketId = searchParams.get('ticketId');
  const [authLoading, setAuthLoading] = useState(true);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submittingAction, setSubmittingAction] = useState('');
  const [session, setSession] = useState(() => telegramMiniAppService.getStoredSession());
  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(initialTicketId || null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [activePanel, setActivePanel] = useState(PANELS.OVERVIEW);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [updateText, setUpdateText] = useState('');
  const [resolveText, setResolveText] = useState('');
  const [holdComment, setHoldComment] = useState('');
  const [resolveComment, setResolveComment] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [solutionFile, setSolutionFile] = useState(null);
  const [flashMessage, setFlashMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [launchBlocker, setLaunchBlocker] = useState(null);
  const [startupStatus, setStartupStatus] = useState('جارٍ تجهيز Telegram Mini App...');
  const telegramWebAppRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const solutionInputRef = useRef(null);
  const flashTimerRef = useRef(null);

  const clearFlashLater = () => {
    window.clearTimeout(flashTimerRef.current);
    flashTimerRef.current = window.setTimeout(() => setFlashMessage(''), 2800);
  };

  const setFlash = (message) => {
    setErrorMessage('');
    setFlashMessage(message);
    clearFlashLater();
  };

  const runProtectedAction = async (actionKey, action) => {
    setErrorMessage('');
    setSubmittingAction(actionKey);

    try {
      await action();
    } catch (error) {
      setErrorMessage(error.response?.data?.error || error.message || 'تعذر تنفيذ الإجراء');
    } finally {
      setSubmittingAction('');
    }
  };

  const loadTickets = async ({ preserveSelection = true } = {}) => {
    setTicketsLoading(true);
    try {
      const rows = await telegramMiniAppService.getMyTickets();
      startTransition(() => {
        setTickets(rows);
      });

      if (!preserveSelection && rows.length > 0 && !selectedTicketId) {
        setSelectedTicketId(String(rows[0].id));
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'تعذر تحميل التذاكر');
    } finally {
      setTicketsLoading(false);
    }
  };

  const loadTicketDetail = async (ticketId, panel = null) => {
    if (!ticketId) {
      setSelectedTicket(null);
      return;
    }

    setDetailLoading(true);
    setErrorMessage('');

    try {
      const detail = await telegramMiniAppService.getTicketById(ticketId);
      startTransition(() => {
        setSelectedTicket(detail);
        setSelectedTicketId(String(ticketId));
        if (panel) {
          setActivePanel(panel);
        }
      });
    } catch (error) {
      setErrorMessage(error.response?.data?.error || 'تعذر تحميل تفاصيل التذكرة');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshCurrentTicket = async ({ keepPanel = true } = {}) => {
    if (!selectedTicketId) {
      await loadTickets();
      return;
    }

    await Promise.all([
      loadTickets(),
      loadTicketDetail(selectedTicketId, keepPanel ? activePanel : PANELS.OVERVIEW)
    ]);
  };

  const handleAttachmentDownload = async (event, attachment) => {
    event.preventDefault();
    if (!selectedTicket) {
      return;
    }

    setErrorMessage('');

    try {
      await telegramMiniAppService.downloadAttachment(
        selectedTicket.id,
        attachment.id,
        attachment.file_name
      );
    } catch (error) {
      setErrorMessage(error.response?.data?.error || error.message || 'تعذر تنزيل المرفق');
    }
  };

  const handleSolutionAttachmentDownload = async (event) => {
    event.preventDefault();
    if (!selectedTicket) {
      return;
    }

    setErrorMessage('');

    try {
      await telegramMiniAppService.downloadSolutionAttachment(selectedTicket.id);
    } catch (error) {
      setErrorMessage(error.response?.data?.error || error.message || 'تعذر تنزيل مرفق الحل');
    }
  };

  useEffect(() => {
    let cancelled = false;
    let timerId = null;
    let attempts = 0;
    const startedAt = Date.now();

    const blockLaunch = ({ type, title, description, reason, hint = '' }) => {
      logMiniAppStage('launch_blocked', { type, reason, attempts });
      if (cancelled) {
        return;
      }

      telegramMiniAppService.clearSession();
      setSession(null);
      setLaunchBlocker({ type, title, description, reason, hint });
      setAuthLoading(false);
    };

    const finalizeAuthenticatedLaunch = async (nextSession) => {
      if (cancelled) {
        return;
      }

      const nextTicketId = initialTicketId || parseTicketIdFromStartParam(nextSession?.telegram?.start_param);
      if (nextTicketId) {
        setSelectedTicketId(String(nextTicketId));
      }

      setLaunchBlocker(null);
      setSession(nextSession);
      setAuthLoading(false);
      await loadTickets({ preserveSelection: false });
    };

    const tryLaunch = async () => {
      attempts += 1;

      const telegram = window.Telegram?.WebApp || null;
      const initData = telegram?.initData || '';
      const elapsedMs = Date.now() - startedAt;
      const hasTelegramContext = Boolean(telegram);
      const hasInitData = Boolean(initData);

      telegramWebAppRef.current = telegram;

      if (attempts === 1 || hasInitData || elapsedMs >= TELEGRAM_BOOT_TIMEOUT_MS) {
        logMiniAppStage('launch_probe', {
          attempts,
          elapsedMs,
          hasTelegramContext,
          hasInitData,
          href: window.location.href
        });
      }

      if (!telegram || !initData) {
        if (!telegram) {
          setStartupStatus('جارٍ انتظار Telegram WebApp...');
        } else {
          setStartupStatus('جارٍ انتظار بيانات التحقق من Telegram...');
          applyTelegramTheme(telegram.themeParams || {});

          try {
            telegram.ready();
            telegram.expand();
          } catch {
            // Telegram client differences should not block the app.
          }
        }

        if (elapsedMs < TELEGRAM_BOOT_TIMEOUT_MS) {
          timerId = window.setTimeout(() => {
            tryLaunch();
          }, TELEGRAM_BOOT_POLL_INTERVAL_MS);
          return;
        }

        blockLaunch({
          type: 'external',
          title: 'افتح التطبيق من داخل البوت',
          description: 'يجب فتح هذه الواجهة من زر "فتح التطبيق" داخل Telegram حتى تصل بيانات التحقق تلقائيًا.',
          reason: !telegram ? 'telegram_sdk_missing' : 'telegram_init_data_missing',
          hint: !telegram
            ? 'إذا كنت داخل Telegram أصلًا، فالغالب أن الصفحة الحالية فتحت كرابط خارجي وليس من Mini App.'
            : 'تم فتح الصفحة داخل Telegram لكن بدون initData، وهذا يحدث عادة عند الدخول من رابط عادي بدل زر Mini App.'
        });
        return;
      }

      applyTelegramTheme(telegram.themeParams || {});

      try {
        telegram.ready();
        telegram.expand();
        if (typeof telegram.requestFullscreen === 'function') {
          telegram.requestFullscreen();
        }
      } catch {
        // Telegram client differences should not block the app.
      }

      setStartupStatus('جارٍ التحقق من حساب Telegram...');
      logMiniAppStage('auth_request', {
        attempts,
        initDataLength: initData.length,
        hasStartParam: Boolean(telegram.initDataUnsafe?.start_param)
      });

      try {
        const nextSession = await telegramMiniAppService.authenticate(initData);
        logMiniAppStage('auth_success', {
          userId: nextSession?.user?.id,
          telegramUserId: nextSession?.telegram?.user_id,
          startParam: nextSession?.telegram?.start_param || null
        });
        await finalizeAuthenticatedLaunch(nextSession);
      } catch (error) {
        const authError = error.response?.data?.error || 'فشل التحقق من هوية Telegram';
        logMiniAppStage('auth_failed', {
          status: error.response?.status,
          message: authError
        });
        blockLaunch({
          type: 'auth',
          title: 'تعذر التحقق من الحساب',
          description: authError,
          reason: 'telegram_auth_failed',
          hint: 'تأكد من ربط حساب Telegram بالمستخدم الصحيح، ثم افتح التطبيق مجددًا من داخل البوت.'
        });
      }
    };

    tryLaunch();

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedTicketId || !session?.token) {
      return;
    }

    loadTicketDetail(selectedTicketId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicketId, session?.token]);

  useEffect(() => {
    const telegram = telegramWebAppRef.current;
    if (!telegram?.BackButton) {
      return undefined;
    }

    if (selectedTicketId) {
      telegram.BackButton.show();
      const handler = () => {
        setSelectedTicketId(null);
        setSelectedTicket(null);
        setActivePanel(PANELS.OVERVIEW);
      };
      telegram.BackButton.onClick(handler);
      return () => {
        telegram.BackButton.offClick(handler);
        telegram.BackButton.hide();
      };
    }

    telegram.BackButton.hide();
    return undefined;
  }, [selectedTicketId]);

  useEffect(() => () => {
    window.clearTimeout(flashTimerRef.current);
  }, []);

  useEffect(() => {
    const candidateTicketId = initialTicketId || parseTicketIdFromStartParam(session?.telegram?.start_param);
    if (!candidateTicketId || !tickets.length) {
      return;
    }

    const matched = tickets.some((ticket) => String(ticket.id) === String(candidateTicketId));
    if (matched) {
      setSelectedTicketId(String(candidateTicketId));
    }
  }, [initialTicketId, session?.telegram?.start_param, tickets]);

  useEffect(() => {
    setUpdateText('');
    setResolveText('');
    setHoldComment('');
    setResolveComment('');
    setAttachmentFile(null);
    setSolutionFile(null);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
    if (solutionInputRef.current) {
      solutionInputRef.current.value = '';
    }
    setErrorMessage('');
    setFlashMessage('');
    setActivePanel(PANELS.OVERVIEW);
  }, [selectedTicketId]);

  const filteredTickets = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return tickets.filter((ticket) => {
      const statusMatches = statusFilter === 'all'
        ? true
        : statusFilter === 'new'
          ? ['new', 'assigned'].includes(ticket.status)
        : statusFilter === 'resolved'
          ? ['resolved', 'closed'].includes(ticket.status)
          : ticket.status === statusFilter;
      const searchMatches = normalizedSearch
        ? getTicketSearchValue(ticket).includes(normalizedSearch)
        : true;

      return statusMatches && searchMatches;
    });
  }, [searchTerm, statusFilter, tickets]);

  const ticketStats = useMemo(() => ({
    total: tickets.length,
    newCount: tickets.filter((ticket) => ticket.status === 'new' || ticket.status === 'assigned').length,
    workingCount: tickets.filter((ticket) => ticket.status === 'in_progress').length,
    holdCount: tickets.filter((ticket) => ticket.status === 'on_hold').length,
    resolvedCount: tickets.filter((ticket) => ticket.status === 'resolved' || ticket.status === 'closed').length
  }), [tickets]);

  const openPanel = (panel) => {
    setActivePanel(panel);
    setErrorMessage('');
  };

  async function handleAccept() {
    await runProtectedAction('accept', async () => {
      await telegramMiniAppService.acceptTicket(selectedTicket.id);
      setFlash('تم استلام التذكرة بنجاح');
      await refreshCurrentTicket({ keepPanel: false });
    });
  }

  async function handleStart() {
    await runProtectedAction('start', async () => {
      await telegramMiniAppService.startTicket(selectedTicket.id);
      setFlash('تم تحويل التذكرة إلى قيد العمل');
      await refreshCurrentTicket({ keepPanel: false });
    });
  }

  async function handleAddUpdate() {
    if (!updateText.trim()) {
      setErrorMessage('أدخل التحديث أولًا');
      return;
    }

    await runProtectedAction('update', async () => {
      await telegramMiniAppService.addComment(selectedTicket.id, updateText.trim());
      setUpdateText('');
      setHoldComment('');
      setFlash('تم حفظ التحديث');
      await refreshCurrentTicket();
      setActivePanel(PANELS.OVERVIEW);
    });
  }

  async function handleAttachmentUpload() {
    if (!attachmentFile) {
      setErrorMessage('اختر ملفًا أولًا');
      return;
    }

    await runProtectedAction('attachment', async () => {
      await telegramMiniAppService.uploadAttachment(selectedTicket.id, attachmentFile);
      setAttachmentFile(null);
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = '';
      }
      setFlash('تم رفع المرفق');
      await refreshCurrentTicket();
    });
  }

  async function handleResolve() {
    if (!resolveText.trim()) {
      setErrorMessage('اكتب ملخص الحل أولًا');
      return;
    }

    await runProtectedAction('resolve', async () => {
      await telegramMiniAppService.addSolution(selectedTicket.id, resolveText.trim());

      if (solutionFile) {
        await telegramMiniAppService.uploadSolutionAttachment(selectedTicket.id, solutionFile);
      }

      await telegramMiniAppService.updateStatus(
        selectedTicket.id,
        'resolved',
        resolveComment.trim() || undefined
      );

      setResolveText('');
      setSolutionFile(null);
      setResolveComment('');
      if (solutionInputRef.current) {
        solutionInputRef.current.value = '';
      }
      setFlash('تم تسجيل الحل وتحويل التذكرة إلى محلولة');
      await refreshCurrentTicket({ keepPanel: false });
    });
  }

  async function handleOnHold() {
    await runProtectedAction('hold', async () => {
      await telegramMiniAppService.updateStatus(
        selectedTicket.id,
        'on_hold',
        holdComment.trim() || 'تم تعليق التذكرة مؤقتًا من Telegram Mini App'
      );
      setHoldComment('');
      setFlash('تم تحويل التذكرة إلى معلقة');
      await refreshCurrentTicket({ keepPanel: false });
    });
  }

  const availablePanels = useMemo(() => {
    const panels = [
      { key: PANELS.OVERVIEW, label: 'التفاصيل والنشاط' }
    ];

    if (selectedTicket?.capabilities?.can_add_update) {
      panels.push({ key: PANELS.UPDATE, label: 'إضافة تحديث' });
    }

    if (selectedTicket?.capabilities?.can_resolve) {
      panels.push({ key: PANELS.RESOLVE, label: 'حل التذكرة' });
    }

    return panels;
  }, [selectedTicket?.capabilities?.can_add_update, selectedTicket?.capabilities?.can_resolve]);

  useEffect(() => {
    if (!availablePanels.some((panel) => panel.key === activePanel)) {
      setActivePanel(PANELS.OVERVIEW);
    }
  }, [activePanel, availablePanels]);

  const quickActions = [];

  if (selectedTicket?.capabilities?.can_accept) {
    quickActions.push({
      key: 'accept',
      label: 'استلام المهمة',
      pendingLabel: 'جارٍ الاستلام...',
      icon: <FaCheckCircle />,
      onClick: handleAccept
    });
  }

  if (selectedTicket?.capabilities?.can_start) {
    quickActions.push({
      key: 'start',
      label: 'بدء العمل',
      pendingLabel: 'جارٍ بدء العمل...',
      icon: <FaPlay />,
      onClick: handleStart
    });
  }

  if (authLoading) {
    return (
      <div className="tg-mini-app-shell">
        <div className="tg-mini-app-loading">
          <div className="tg-mini-app-spinner" />
          <div>
            <h2>تهيئة الواجهة</h2>
            <p>{startupStatus}</p>
          </div>
        </div>
      </div>
    );
  }

  if (launchBlocker) {
    const botUrl = getTelegramBotUrl();

    return (
      <div className="tg-mini-app-shell">
        <div className="tg-mini-app-empty-state">
          <div className="tg-mini-app-empty-icon">
            <FaTicketAlt />
          </div>
          <h2>{launchBlocker.title}</h2>
          <p>{launchBlocker.description}</p>
          {launchBlocker.hint ? (
            <p className="tg-mini-app-muted">{launchBlocker.hint}</p>
          ) : null}
          <div className="tg-mini-app-empty-actions">
            {botUrl ? (
              <a
                className="tg-btn tg-btn-primary"
                href={botUrl}
                target="_blank"
                rel="noreferrer"
              >
                فتح البوت
              </a>
            ) : null}
            <button
              type="button"
              className="tg-btn tg-btn-secondary"
              onClick={() => window.location.reload()}
            >
              إعادة المحاولة
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tg-mini-app-shell">
      <header className="tg-mini-app-header">
        <div className="tg-mini-app-header-copy">
          <div className="tg-mini-app-kicker">Telegram Mini App</div>
          <h1>لوحة التذاكر التشغيلية</h1>
          <p>{session?.user?.full_name || 'الفني'} <span>•</span> {getRoleLabel(session?.user?.role)}</p>
        </div>
        <button
          type="button"
          className="tg-mini-app-refresh"
          onClick={() => refreshCurrentTicket({ keepPanel: true })}
          disabled={ticketsLoading || detailLoading}
        >
          {ticketsLoading || detailLoading ? 'جارٍ التحديث...' : 'تحديث القائمة'}
        </button>
      </header>

      <section className="tg-mini-app-stats">
        <StatCard icon={<FaTicketAlt />} label="كل التذاكر" value={ticketStats.total} accent="cyan" />
        <StatCard icon={<FaCircle />} label="جديدة" value={ticketStats.newCount} accent="gold" />
        <StatCard icon={<FaPlay />} label="قيد العمل" value={ticketStats.workingCount} accent="teal" />
        <StatCard icon={<FaCheckCircle />} label="منتهية" value={ticketStats.resolvedCount} accent="slate" />
      </section>

      <div className="tg-mini-app-layout">
        <aside className={`tg-mini-app-sidebar ${selectedTicketId ? 'has-selection' : ''}`}>
          <div className="tg-mini-app-sidebar-head">
            <div>
              <h2>تذاكري</h2>
              <p>{filteredTickets.length} من أصل {ticketStats.total} تذكرة</p>
            </div>
            {ticketStats.holdCount > 0 ? (
              <div className="tg-mini-app-sidebar-chip">المعلقة {ticketStats.holdCount}</div>
            ) : null}
          </div>

          <div className="tg-mini-app-toolbar">
            <div className="tg-mini-app-search">
              <FaSearch />
              <input
                type="search"
                placeholder="بحث في الرقم أو العنوان أو الفرع"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className="tg-mini-app-filters">
              <FilterPill active={statusFilter === 'all'} label="الكل" onClick={() => setStatusFilter('all')} />
              <FilterPill active={statusFilter === 'new'} label="جديدة ومسندة" onClick={() => setStatusFilter('new')} />
              <FilterPill active={statusFilter === 'in_progress'} label="قيد العمل" onClick={() => setStatusFilter('in_progress')} />
              <FilterPill active={statusFilter === 'on_hold'} label="معلقة" onClick={() => setStatusFilter('on_hold')} />
              <FilterPill active={statusFilter === 'resolved'} label="محلولة" onClick={() => setStatusFilter('resolved')} />
            </div>
          </div>

          {ticketsLoading ? (
            <div className="tg-mini-app-list-skeleton">
              {[1, 2, 3].map((item) => <div key={item} className="tg-mini-app-skeleton-card" />)}
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="tg-mini-app-empty-state small">
              <div className="tg-mini-app-empty-icon small">
                <FaUserCog />
              </div>
              <h3>لا توجد تذاكر مطابقة</h3>
              <p>جرّب تغيير الفلاتر أو البحث بكلمات أقل، وستظهر النتائج هنا مباشرة.</p>
            </div>
          ) : (
            <div className="tg-mini-app-ticket-list">
              {filteredTickets.map((ticket) => (
                <button
                  key={ticket.id}
                  type="button"
                  className={`tg-mini-ticket-card ${String(selectedTicketId) === String(ticket.id) ? 'active' : ''}`}
                  onClick={() => setSelectedTicketId(String(ticket.id))}
                >
                  <div className="tg-mini-ticket-card-top">
                    <span className={`tg-mini-status tg-status-${ticket.status}`}>{getStatusLabel(ticket.status)}</span>
                    <span className={`tg-mini-priority tg-priority-${ticket.priority}`}>{getPriorityLabel(ticket.priority)}</span>
                  </div>
                  <div className="tg-mini-ticket-card-id">#{ticket.ticket_number || ticket.id}</div>
                  <h3>{ticket.short_description || 'بدون عنوان'}</h3>
                  <p>{ticket.branch_name || 'بدون فرع'} • {translateIssueTypes(ticket.issue_types)}</p>
                  <div className="tg-mini-ticket-meta">
                    <span><FaClock /> {formatDateTime(ticket.updated_at || ticket.received_date)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </aside>

        <main className={`tg-mini-app-content ${selectedTicketId ? 'open' : ''}`}>
          {!selectedTicketId ? (
            <div className="tg-mini-app-empty-state">
              <div className="tg-mini-app-empty-icon">
                <FaWrench />
              </div>
              <h2>اختر تذكرة للمتابعة</h2>
              <p>ستظهر هنا تفاصيل التذكرة، النشاطات الأخيرة، المرفقات، والتحديثات المتاحة.</p>
            </div>
          ) : detailLoading || !selectedTicket ? (
            <div className="tg-mini-app-detail-loading">
              <div className="tg-mini-app-spinner" />
              <div>
                <h2>تحميل تفاصيل التذكرة</h2>
                <p>نجهّز لك الشاشة المختصرة المناسبة للهاتف.</p>
              </div>
            </div>
          ) : (
            <>
              <section className="tg-mini-app-detail-hero">
                <div className="tg-mini-app-detail-header">
                  <button
                    type="button"
                    className="tg-mini-app-back-mobile"
                    onClick={() => {
                      setSelectedTicketId(null);
                      setSelectedTicket(null);
                    }}
                  >
                    <FaArrowLeft />
                  </button>

                  <div className="tg-mini-app-detail-headline">
                    <div className="tg-mini-app-detail-topline">
                      <span className={`tg-mini-status tg-status-${selectedTicket.status}`}>{getStatusLabel(selectedTicket.status)}</span>
                      <span className={`tg-mini-priority tg-priority-${selectedTicket.priority}`}>{getPriorityLabel(selectedTicket.priority)}</span>
                    </div>
                    <h2>{selectedTicket.short_description || 'بدون عنوان'}</h2>
                    <p className="tg-mini-app-ticket-number">#{selectedTicket.ticket_number || selectedTicket.id}</p>
                  </div>
                </div>

                <div className="tg-mini-app-hero-meta">
                  <DetailMetaCard label="الفرع" value={selectedTicket.branch_name || '-'} />
                  <DetailMetaCard label="النوع" value={translateIssueTypes(selectedTicket.issue_types)} />
                  <DetailMetaCard label="آخر تحديث" value={formatDateTime(selectedTicket.updated_at || selectedTicket.received_date)} />
                  <DetailMetaCard
                    label="الفنيون المسندون"
                    value={selectedTicket.assignees?.length ? selectedTicket.assignees.map((item) => item.full_name).join('، ') : 'غير محدد'}
                  />
                </div>

                {quickActions.length ? (
                  <div className="tg-mini-app-quick-actions">
                    {quickActions.map((action) => (
                      <button
                        key={action.key}
                        type="button"
                        className="tg-btn tg-btn-secondary tg-btn-quick"
                        onClick={action.onClick}
                        disabled={submittingAction === action.key}
                      >
                        {action.icon}
                        <span>{submittingAction === action.key ? action.pendingLabel : action.label}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </section>

              {flashMessage ? (
                <FeedbackBanner variant="success" text={flashMessage} />
              ) : null}

              {errorMessage ? (
                <FeedbackBanner variant="error" text={errorMessage} />
              ) : null}

              <div className="tg-mini-app-panel-tabs">
                {availablePanels.map((panel) => (
                  <PanelTab
                    key={panel.key}
                    active={activePanel === panel.key}
                    label={panel.label}
                    onClick={() => openPanel(panel.key)}
                  />
                ))}
              </div>

              <section className="tg-mini-app-panel">
                {activePanel === PANELS.OVERVIEW ? (
                  <div className="tg-mini-app-overview-grid">
                    <div className="tg-mini-app-column">
                      <div className="tg-mini-app-section-card">
                        <SectionHeading
                          title="آخر النشاطات"
                          text="تسلسل واضح لآخر ما حدث على التذكرة، مناسب للقراءة السريعة داخل الهاتف."
                        />

                        {(selectedTicket.activity || []).length === 0 ? (
                          <div className="tg-mini-app-inline-empty">
                            <div className="tg-mini-app-inline-icon">
                              <FaInbox />
                            </div>
                            <div>
                              <strong>لا توجد نشاطات مسجلة بعد</strong>
                              <p>أي تحديث أو مرفق أو حل جديد سيظهر هنا مباشرة.</p>
                            </div>
                          </div>
                        ) : (
                          <div className="tg-mini-app-activity-list">
                            {selectedTicket.activity.map((item) => (
                              <article key={item.id} className="tg-mini-activity-item">
                                <div className={`tg-mini-activity-badge ${activityClassName(item.type)}`}>
                                  {activityIcon(item.type)}
                                </div>
                                <div className="tg-mini-activity-content">
                                  <div className="tg-mini-activity-topline">
                                    <span className="tg-mini-activity-type">{activityLabel(item.type)}</span>
                                    <time>{formatDateTime(item.created_at)}</time>
                                  </div>
                                  <strong>{item.actor_name || 'النظام'}</strong>
                                  <p>{item.text}</p>
                                </div>
                              </article>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="tg-mini-app-column tg-mini-app-column-side">
                      <div className="tg-mini-app-section-card">
                        <SectionHeading
                          title="الفنيون المسندون"
                          text="الأسماء الظاهرة هنا هي المسؤولة حاليًا عن متابعة التذكرة."
                        />

                        {(selectedTicket.assignees || []).length === 0 ? (
                          <p className="tg-mini-app-muted">لا يوجد فني مسند ظاهر في هذه اللحظة.</p>
                        ) : (
                          <div className="tg-mini-app-assignees">
                            {selectedTicket.assignees.map((assignee) => (
                              <div key={assignee.id} className="tg-mini-app-assignee-chip">
                                <span>{assignee.full_name}</span>
                                <small>{assignee.is_primary ? 'المسؤول الأساسي' : getRoleLabel(assignee.role)}</small>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="tg-mini-app-section-card">
                        <SectionHeading
                          title="المرفقات"
                          text="كل المرفقات الحالية في مكان واحد، مع رفع سريع من نفس الشاشة."
                        />

                        {(selectedTicket.attachments || []).length === 0 ? (
                          <div className="tg-mini-app-inline-empty compact">
                            <div className="tg-mini-app-inline-icon">
                              <FaPaperclip />
                            </div>
                            <div>
                              <strong>لا توجد مرفقات بعد</strong>
                              <p>يمكنك إضافة أول مرفق من النموذج أدناه إذا كانت الصلاحية متاحة.</p>
                            </div>
                          </div>
                        ) : (
                          <div className="tg-mini-app-link-list">
                            {selectedTicket.attachments.map((attachment) => (
                              <a
                                key={attachment.id}
                                href="#attachment-download"
                                onClick={(event) => handleAttachmentDownload(event, attachment)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <FaPaperclip />
                                <span>{attachment.file_name}</span>
                              </a>
                            ))}
                          </div>
                        )}

                        {selectedTicket.solution_attachment ? (
                          <a
                            className="tg-mini-app-solution-link"
                            href="#solution-attachment-download"
                            onClick={handleSolutionAttachmentDownload}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <FaUpload />
                            <span>عرض مرفق الحل</span>
                          </a>
                        ) : null}

                        {selectedTicket.capabilities?.can_upload_attachment ? (
                          <div className="tg-mini-app-upload-box">
                            <label className="tg-mini-app-file-picker">
                              <span>{attachmentFile ? attachmentFile.name : 'اختر ملفًا لإرفاقه'}</span>
                              <input
                                ref={attachmentInputRef}
                                type="file"
                                onChange={(event) => setAttachmentFile(event.target.files?.[0] || null)}
                              />
                            </label>

                            <button
                              type="button"
                              className="tg-btn tg-btn-primary"
                              onClick={handleAttachmentUpload}
                              disabled={submittingAction === 'attachment'}
                            >
                              {submittingAction === 'attachment' ? 'جارٍ رفع المرفق...' : 'رفع المرفق'}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}

                {activePanel === PANELS.UPDATE ? (
                  <div className="tg-mini-app-form-grid">
                    <div className="tg-mini-app-form-card">
                      <SectionHeading
                        title="إضافة تحديث تشغيلي"
                        text="أضف ملخصًا قصيرًا وواضحًا يشرح ما تم إنجازه أو ما يمنع إكمال المهمة."
                      />

                      <textarea
                        value={updateText}
                        onChange={(event) => setUpdateText(event.target.value)}
                        placeholder="اكتب آخر ما تم إنجازه أو ما تحتاجه لإكمال المهمة..."
                      />

                      <textarea
                        value={holdComment}
                        onChange={(event) => setHoldComment(event.target.value)}
                        placeholder="ملاحظة إضافية عند التحويل إلى معلقة (اختياري)"
                      />

                      <div className="tg-mini-app-form-actions">
                        <button
                          type="button"
                          className="tg-btn tg-btn-primary"
                          onClick={handleAddUpdate}
                          disabled={submittingAction === 'update'}
                        >
                          {submittingAction === 'update' ? 'جارٍ حفظ التحديث...' : 'حفظ التحديث'}
                        </button>

                        <button
                          type="button"
                          className="tg-btn tg-btn-secondary"
                          onClick={handleOnHold}
                          disabled={submittingAction === 'hold'}
                        >
                          {submittingAction === 'hold' ? 'جارٍ التحويل...' : 'تحويل إلى معلقة'}
                        </button>
                      </div>
                    </div>

                    <div className="tg-mini-app-section-card tg-mini-app-tip-card">
                      <SectionHeading
                        title="متى أستخدم هذه الشاشة؟"
                        text="مناسبة للتقدم التشغيلي، طلب الدعم من الفريق، أو توثيق سبب التعليق المؤقت."
                      />
                      <ul className="tg-mini-app-tip-list">
                        <li>اكتب سطرًا واحدًا واضحًا بدل فقرة طويلة.</li>
                        <li>استخدم التحويل إلى معلقة فقط إذا كان هناك سبب فعلي يمنع الاستمرار.</li>
                        <li>بعد الحفظ سيظهر التحديث تلقائيًا في سجل النشاط.</li>
                      </ul>
                    </div>
                  </div>
                ) : null}

                {activePanel === PANELS.RESOLVE ? (
                  <div className="tg-mini-app-form-grid">
                    <div className="tg-mini-app-form-card">
                      <SectionHeading
                        title="حل التذكرة"
                        text="سجل ملخص الحل النهائي بصياغة مختصرة، ويمكنك إرفاق ملف حل عند الحاجة."
                      />

                      <textarea
                        value={resolveText}
                        onChange={(event) => setResolveText(event.target.value)}
                        placeholder="اكتب الحل النهائي بصياغة واضحة ومختصرة..."
                      />

                      <label className="tg-mini-app-file-picker">
                        <span>{solutionFile ? solutionFile.name : 'إرفاق ملف الحل (اختياري)'}</span>
                        <input
                          ref={solutionInputRef}
                          type="file"
                          onChange={(event) => setSolutionFile(event.target.files?.[0] || null)}
                        />
                      </label>

                      <textarea
                        value={resolveComment}
                        onChange={(event) => setResolveComment(event.target.value)}
                        placeholder="تعليق إغلاق إضافي (اختياري)"
                      />

                      <button
                        type="button"
                        className="tg-btn tg-btn-primary"
                        onClick={handleResolve}
                        disabled={submittingAction === 'resolve'}
                      >
                        {submittingAction === 'resolve' ? 'جارٍ حفظ الحل...' : 'حفظ الحل وتحويلها إلى محلولة'}
                      </button>
                    </div>

                    <div className="tg-mini-app-section-card tg-mini-app-tip-card">
                      <SectionHeading
                        title="قبل الإنهاء"
                        text="تأكد أن الملخص كافٍ ليُفهم لاحقًا من داخل التذكرة بدون الرجوع لمحادثات خارجية."
                      />
                      <ul className="tg-mini-app-tip-list">
                        <li>اذكر ما تم إصلاحه أو تغييره بالفعل.</li>
                        <li>أرفق ملف الحل فقط إذا كان مفيدًا للفريق لاحقًا.</li>
                        <li>بعد الحفظ ستبقى شاشة التفاصيل متاحة للمراجعة بدون الأزرار القديمة.</li>
                      </ul>
                    </div>
                  </div>
                ) : null}
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, accent }) => (
  <div className={`tg-mini-stat-card accent-${accent}`}>
    <div className="tg-mini-stat-icon">{icon}</div>
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  </div>
);

const FilterPill = ({ active, label, onClick }) => (
  <button type="button" className={`tg-filter-pill ${active ? 'active' : ''}`} onClick={onClick}>
    {label}
  </button>
);

const PanelTab = ({ active, label, onClick }) => (
  <button type="button" className={`tg-panel-tab ${active ? 'active' : ''}`} onClick={onClick}>
    {label}
  </button>
);

const DetailMetaCard = ({ label, value }) => (
  <div className="tg-mini-app-detail-meta-card">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const SectionHeading = ({ title, text }) => (
  <div className="tg-mini-app-section-heading">
    <h3>{title}</h3>
    <p>{text}</p>
  </div>
);

const FeedbackBanner = ({ variant, text }) => (
  <div className={`tg-mini-app-flash ${variant}`}>
    <div className="tg-mini-app-flash-icon">
      {variant === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
    </div>
    <p>{text}</p>
  </div>
);

const activityLabel = (type) => {
  switch (type) {
    case 'comment':
      return 'تحديث';
    case 'attachment':
      return 'مرفق';
    case 'resolution':
      return 'حل';
    default:
      return 'نشاط';
  }
};

const activityClassName = (type) => {
  switch (type) {
    case 'attachment':
      return 'attachment';
    case 'resolution':
      return 'resolution';
    default:
      return 'comment';
  }
};

const activityIcon = (type) => {
  switch (type) {
    case 'attachment':
      return <FaPaperclip />;
    case 'resolution':
      return <FaCheckCircle />;
    default:
      return <FaCircle />;
  }
};

export default TelegramMiniApp;
